import Sortable from "sortablejs";
import { applySortableReorder, createReorderable } from "../shared/sortable-reorder";
import { dispatchToast, getCSRFToken, processResponseTriggers } from "../shared/htmx-helpers";

// One selectable image in the curation modal. Mirrors components.CurationCandidate
// (and the curate POST DTO, api.curateItem). `_k` is a client-only stable key the
// controller assigns so x-for keying + reorder + match are robust even when two
// candidates share a url/key (e.g. before dedup).
interface Candidate {
  _k?: number;
  origin: string;
  existing_image_id?: string;
  source_type?: string;
  source_id?: string;
  url?: string;
  storage_key?: string;
  label?: string;
  is_hero?: boolean;
  analyzing?: boolean;
  // media-04: "too_large" on a canonical candidate whose source exceeds the
  // decode budget — its url is the raw original, so no thumbnail loads it.
  processing_error?: string;
  width?: number;
  height?: number;
}

interface BlockedRef {
  website?: string;
  section?: string;
  url?: string;
}

interface CurationConfig {
  candidates: Candidate[];
  baseURL: string; // property-hub base; curate POSTs to `${baseURL}/images/curate`
  slug: string;
  // chip-key → localized label (server-built with t(...), threaded via x-data so
  // no copy is hardcoded in TS and there is no window.* global).
  chipLabels: Record<string, string>;
  // Localized toast shown when a non-409 save fails (server-built with t(),
  // threaded via x-data — same no-hardcoded-copy contract as chipLabels).
  saveFailedMsg: string;
}

export function propertyCurationModal(config: CurationConfig) {
  let _seq = 0;
  const all: Candidate[] = (config.candidates || []).map((c) => ({ ...c, _k: _seq++ }));
  return {
    baseURL: config.baseURL,
    slug: config.slug,
    chipLabels: config.chipLabels || {},
    saveFailedMsg: config.saveFailedMsg || "",
    all,
    // Pre-selected = the already-canonical subset, in server order (which is the
    // candidate order: canonical rows are emitted first, position-ordered).
    selected: all.filter((c) => !!c.existing_image_id) as Candidate[],
    // Distinct origins present (chip keys), in candidate order.
    origins: Array.from(new Set(all.map((c) => c.origin))),
    activeChip: "all",
    saving: false,
    blockedRefs: [] as BlockedRef[],
    _sortable: null as Sortable | null,

    // Register the open() handle in the `curation` Alpine store so the Manage-tab
    // Curate button (outside this component) can open the dialog.
    register() {
      (window as any).Alpine?.store("curation", { open: () => this.open() });
    },
    // Sync candidate "analysing" flags from a property-images-analyzing broadcast
    // (dispatched by the grid-region poll on every render). Property-image uploads
    // publish no analysis-complete SSE, so this is how an OPEN modal's canonical
    // tiles clear their spinner without re-fetching (which would close the dialog).
    // Canonical candidates match by existing_image_id; selected shares the same
    // object refs (filtered from all), so both panes update reactively.
    updateAnalyzing(ids: string[]) {
      const set = new Set(ids || []);
      this.all.forEach((c) => {
        if (c.existing_image_id) c.analyzing = set.has(c.existing_image_id);
      });
    },
    open() {
      this.blockedRefs = [];
      ((this as any).$refs.dlg as HTMLDialogElement).showModal();
      (this as any).$nextTick(() => this.initSortable());
    },
    close() {
      this.teardown();
      ((this as any).$refs.dlg as HTMLDialogElement).close();
    },
    // Native ESC/backdrop close bypasses close(); the dialog @close routes here so
    // SortableJS is always torn down.
    teardown() {
      if (this._sortable) {
        this._sortable.destroy();
        this._sortable = null;
      }
    },
    initSortable() {
      const el = (this as any).$refs.selectedList as HTMLElement | undefined;
      if (!el) return;
      this.teardown();
      this._sortable = createReorderable(el, {
        handle: "[data-sort-handle]",
        draggable: "[data-id]",
        onEnd: (e: Sortable.SortableEvent) => {
          this.selected = applySortableReorder(el, e, this.selected, "data-id");
        },
      });
    },

    chipOptions(): string[] {
      return ["all", ...this.origins];
    },
    chipText(chip: string): string {
      return this.chipLabels[chip] || chip;
    },
    visibleCandidates(): Candidate[] {
      if (this.activeChip === "all") return this.all;
      return this.all.filter((c) => c.origin === this.activeChip);
    },

    // Identity match: an already-canonical row matches by existing_image_id; a
    // net-new candidate matches by url, then storage_key, then the client key.
    _match(a: Candidate, b: Candidate): boolean {
      if (a.existing_image_id && b.existing_image_id)
        return a.existing_image_id === b.existing_image_id;
      if (a.url && b.url && a.url === b.url) return true;
      if (a.storage_key && b.storage_key && a.storage_key === b.storage_key) return true;
      return a._k === b._k;
    },
    isSelected(c: Candidate): boolean {
      return this.selected.some((s) => this._match(s, c));
    },
    orderOf(c: Candidate): number {
      return this.selected.findIndex((s) => this._match(s, c)) + 1;
    },
    toggle(c: Candidate) {
      if (this.isSelected(c)) {
        this.deselect(c);
        return;
      }
      this.selected = [...this.selected, c];
    },
    deselect(c: Candidate) {
      this.selected = this.selected.filter((s) => !this._match(s, c));
    },
    // Hero is single-select across the canonical set. Mutate is_hero IN PLACE on
    // the existing selected objects — they share identity with `this.all` (selected
    // is filtered from all, toggle() pushes the same refs), and the analysing-spinner
    // sync (updateAnalyzing, comment §64-66) relies on that shared identity. Spreading
    // into new objects would fork the two arrays and desync the panes.
    toggleHero(c: Candidate) {
      const wasHero = !!c.is_hero;
      this.selected.forEach((s) => {
        s.is_hero = this._match(s, c) ? !wasHero : false;
      });
    },
    urlFor(c: Candidate): string {
      // media-04: never load a too-large candidate's raw original.
      if (c.processing_error === "too_large") return "";
      return c.url || "";
    },

    // Save = reconcile: the left-pane order is the desired canonical set (index =
    // position). On 204 the server sent HX-Trigger (toast + property-images-curated);
    // processResponseTriggers re-dispatches both on document.body, so the section
    // wrapper's hx-trigger="property-images-curated from:body" re-fetches the grid +
    // modal candidate data. On 409 the image is still referenced — show the blocking
    // refs inline and keep the modal open.
    async save() {
      if (this.saving) return;
      this.saving = true;
      this.blockedRefs = [];
      const items = this.selected.map((c) => ({
        existing_image_id: c.existing_image_id || "",
        source_type: c.source_type || "",
        source_id: c.source_id || "",
        url: c.url || "",
        storage_key: c.storage_key || "",
        is_hero: !!c.is_hero,
      }));
      try {
        const res = await fetch(`${this.baseURL}/images/curate`, {
          method: "POST",
          credentials: "same-origin",
          headers: { "Content-Type": "application/json", "X-CSRF-Token": getCSRFToken() },
          body: JSON.stringify({ items }),
        });
        if (res.status === 409) {
          const body = (await res.json().catch(() => ({}))) as {
            message?: string;
            references?: BlockedRef[];
          };
          this.blockedRefs = body.references || [];
          if (body.message) dispatchToast(body.message, "error");
          this.saving = false;
          return;
        }
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        // 204: re-dispatch the server's HX-Trigger header (flash-message toast +
        // property-images-curated) so the section re-fetches. No full-page refresh.
        processResponseTriggers(res);
        this.saving = false;
        this.close();
      } catch (err) {
        console.error("[curation] save failed", err);
        // Surface the failure to the operator (mirrors the 409 branch above) —
        // a silent console.error leaves them thinking the save succeeded.
        dispatchToast(this.saveFailedMsg, "error");
        this.saving = false;
      }
    },
  };
}
